<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';
import { useVisionSession } from 'dashboard/composables/vision/useVisionSession';
import { useVisionAccess } from 'dashboard/composables/useVisionAccess';
import Vision from 'dashboard/components-next/vision/Vision.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const route = useRoute();
const { uiSettings, updateUISettings } = useUISettings();
const { isEnterprise } = useConfig();

// Vision é efêmero e centrado na conversa: ao trocar de rota (sair da conversa
// ou ir pra outra), fecha o painel. Só abre de novo pelo SidepanelSwitch.
watch(
  () => route.fullPath,
  () => {
    if (uiSettings.value?.is_vision_panel_open) {
      updateUISettings({ is_vision_panel_open: false });
    }
  }
);
const { width: windowWidth } = useWindowSize();

const currentChat = useMapGetter('getSelectedChat');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const { messages, ask, reset } = useVisionSession();

// Vision é do administrador; agente só tem com tools liberadas no papel
// personalizado. Sem isso o painel nem monta (a API responde 403 a toda
// pergunta).
const { visionAllowed } = useVisionAccess();

// Largura do painel: 320/360px é o padrão (contexto ao lado da conversa);
// expandido vai a 640px pra ler cadastro/contratos/títulos sem quebrar tabela.
// Estado local (não persiste no perfil): quem expande é quem está lendo agora.
const isExpanded = ref(false);
const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};

// CPF/CNPJ do contato da conversa atual (custom attribute) — habilita o atalho de
// consulta ao IXC no estado inicial do Vision.
const contactCpf = computed(() => {
  const attributes = currentChat.value?.meta?.sender?.custom_attributes || {};
  return attributes.cpf || attributes.cnpj || '';
});

// Contrato que o cliente escolheu NESTA conversa (Ai::Actions::SetConversationContract,
// gravado pelo turn quando o picker/texto/sticky fixa `selected_id_contrato`).
// Genérico por provedor. Vai no contexto do Vision e nas perguntas prontas.
const selectedContract = computed(
  () => currentChat.value?.additional_attributes?.ai_selected_contract || null
);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const shouldShowVisionPanel = computed(() => {
  if (!isEnterprise || !visionAllowed.value) {
    return false;
  }
  const isVisionEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.VISION
  );
  return isVisionEnabled && uiSettings.value.is_vision_panel_open;
});

const buildContext = () => {
  const sender = currentChat.value?.meta?.sender || {};
  return {
    conversation_id: currentChat.value?.id,
    display_id: currentChat.value?.id,
    contact: {
      name: sender.name,
      cpf: sender.custom_attributes?.cpf,
      cnpj: sender.custom_attributes?.cnpj,
    },
    ...(selectedContract.value
      ? {
          contract: {
            id_contrato: String(selectedContract.value.id_contrato),
            label: selectedContract.value.label || '',
          },
        }
      : {}),
  };
};

const sendMessage = message => ask(message, buildContext());

// Atalho do reply box (AiQuickActionsMenu): abre o painel já perguntando.
// O menu só aparece com a flag `vision` ligada — o mesmo gate deste painel.
const onVisionAsk = ({ prompt }) => {
  if (!uiSettings.value?.is_vision_panel_open) {
    updateUISettings({
      is_vision_panel_open: true,
      is_copilot_panel_open: false,
      is_contact_sidebar_open: false,
    });
  }
  ask(prompt, buildContext());
};

onMounted(() => emitter.on(BUS_EVENTS.VISION_ASK, onVisionAsk));
onBeforeUnmount(() => emitter.off(BUS_EVENTS.VISION_ASK, onVisionAsk));

// Fecha o painel pelo X do header do Vision (desacoplado): replica o
// comportamento anterior, fechando também a sidebar de contato.
const onVisionClose = () => {
  updateUISettings({
    is_vision_panel_open: false,
    is_contact_sidebar_open: false,
  });
};

const closeVisionPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_vision_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_vision_panel_open: false,
    });
  }
};
</script>

<template>
  <div
    v-if="shouldShowVisionPanel"
    v-on-click-outside="() => closeVisionPanel()"
    class="bg-n-background h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full transition-[width,min-width] duration-200 ease-out motion-reduce:transition-none md:static ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowVisionPanel,
        'md:hidden': !shouldShowVisionPanel,
        'max-w-sm md:w-[320px] md:min-w-[320px] 2xl:min-w-[360px] 2xl:w-[360px]':
          !isExpanded,
        'max-w-2xl md:w-[640px] md:min-w-[640px]': isExpanded,
      },
    ]"
  >
    <Vision
      :messages="messages"
      :contact-cpf="contactCpf"
      :selected-contract="selectedContract"
      expandable
      :expanded="isExpanded"
      @send-message="sendMessage"
      @reset="reset"
      @toggle-expand="toggleExpand"
      @close="onVisionClose"
    />
  </div>
  <template v-else />
</template>
